import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  disableNetwork,
  enableNetwork,
  waitForPendingWrites
} from 'firebase/firestore';
import { getAnalytics } from 'firebase/analytics';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';

// Firebase configuration from environment variables
// Falls back to hardcoded values for backwards compatibility
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyB7KDAaMn9KreucWj9PowJ2qM7neL4bbFM",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "pooltournaments.app",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "kcpooltournament",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "kcpooltournament.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "518553153165",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:518553153165:web:cca6592b1f750dc6713cc4",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-769LF7T1M4"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services.
// Firestore uses a persistent (IndexedDB) local cache: reads are served from
// cache (instant reloads, resilient to dropouts) and writes queue offline and
// flush on reconnect — important for tournament directors on weak venue Wi-Fi.
// Combined with the per-instance subcollection, a live score update only syncs
// that ~19 KB occurrence doc instead of the whole tournament.
export const auth = getAuth(app);
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
});
export const analytics = getAnalytics(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

// Force Firestore to tear down and re-establish its backend streams. Used to
// recover a wedged write channel — e.g. after the 1-hour auth token expired
// while the director's laptop was asleep, leaving queued writes stuck in the
// local cache with a green "synced" light. Bouncing the network makes the SDK
// reconnect with a fresh token and flush anything queued offline.
export const forceReconnect = async () => {
  try {
    await disableNetwork(db);
    await enableNetwork(db);
    return true;
  } catch (e) {
    console.warn('Firestore reconnect failed:', e?.message || e);
    return false;
  }
};

// Force-refresh the Firebase Auth ID token. The SDK refreshes it roughly hourly
// on its own, but a sleeping/backgrounded tab can miss that window, leaving
// Firestore writes unauthenticated (they then queue silently). Call proactively
// on a timer and on tab-wake.
export const forceTokenRefresh = async () => {
  try {
    if (auth.currentUser) {
      await auth.currentUser.getIdToken(true);
      return true;
    }
  } catch (e) {
    console.warn('Auth token refresh failed:', e?.message || e);
  }
  return false;
};

// The authoritative "are my edits actually on the server?" primitive. Firestore's
// awaited updateDoc/setDoc does NOT resolve while a write is unacked (it hangs);
// waitForPendingWrites resolves ONLY once the backend has acknowledged every
// pending write for the current user. We race it against a timeout: if it hasn't
// resolved in `timeoutMs`, writes are genuinely stuck (not just the LED lying).
// Returns true if all writes are acked, false if still pending past the timeout.
export const writesAreFlushed = async (timeoutMs = 8000) => {
  try {
    const flushed = waitForPendingWrites(db).then(() => true);
    const timeout = new Promise((resolve) => setTimeout(() => resolve(false), timeoutMs));
    return await Promise.race([flushed, timeout]);
  } catch (e) {
    // Rejected only on user change; treat as "unknown / not flushed".
    return false;
  }
};

export default app;